
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Upload, X, Image, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PhotoUploadChatProps {
  onPhotosSelected: (data: { photos: File[] }) => void;
}

const PhotoUploadChat = ({ onPhotosSelected }: PhotoUploadChatProps) => {
  const { t } = useTranslation('memory');
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [previewErrors, setPreviewErrors] = useState<boolean[]>([]);
  const [isGeneratingPreviews, setIsGeneratingPreviews] = useState(false);

  const isHEIC = useCallback((file: File): boolean => {
    const fileName = file.name.toLowerCase();
    const isHEICExtension = fileName.endsWith('.heic') || fileName.endsWith('.heif');
    const isHEICMimeType = file.type === 'image/heic' || file.type === 'image/heif';
    // iOS sometimes reports empty MIME type for HEIC files
    const isEmptyMimeTypeFromMobile = !file.type && /^img_\d+\.heic?$/i.test(fileName);
    
    return isHEICExtension || isHEICMimeType || isEmptyMimeTypeFromMobile;
  }, []);

  const convertHEICToJPEG = useCallback(async (file: File): Promise<File> => {
    try {
      console.log('📸 Converting HEIC to JPEG:', file.name);
      
      // Lazy load heic-to library only when needed
      const heicTo = await import('heic-to');
      
      const jpegBlob = await heicTo.heicTo({
        blob: file,
        type: 'image/jpeg',
        quality: 0.8
      });
      
      const jpegFile = new File(
        [jpegBlob], 
        file.name.replace(/\.(heic|heif)$/i, '.jpg'),
        { type: 'image/jpeg' }
      );
      
      console.log('📸 HEIC conversion successful:', jpegFile.name);
      return jpegFile;
    } catch (error) {
      console.error('📸 HEIC conversion failed:', error);
      // Return original file as fallback instead of throwing
      console.warn('📸 Falling back to original HEIC file');
      return file;
    }
  }, []);

  const createPreviewUrl = useCallback(async (file: File): Promise<{ url: string | null; error: boolean }> => {
    try {
      let processedFile = file;

      // Convert HEIC files to JPEG first with error handling
      if (isHEIC(file)) {
        try {
          processedFile = await convertHEICToJPEG(file);
          console.log('📸 PhotoUploadChat - HEIC converted for preview:', processedFile.name);
        } catch (error) {
          console.error('📸 PhotoUploadChat - HEIC conversion failed, using original:', error);
          // Continue with original file instead of failing
          processedFile = file;
        }
      }

      // Validate file after potential conversion
      if (!processedFile || !processedFile.type.startsWith('image/')) {
        console.warn('📸 PhotoUploadChat - Invalid file type:', processedFile?.type);
        return { url: null, error: true };
      }

      if (processedFile.size > 50 * 1024 * 1024) { // 50MB limit
        console.warn('📸 PhotoUploadChat - File too large:', processedFile.size);
        return { url: null, error: true };
      }

      // Create object URL with error handling
      const url = URL.createObjectURL(processedFile);
      
      // Test if the URL is valid by creating an image element
      return new Promise<{ url: string | null; error: boolean }>((resolve) => {
        const img = document.createElement('img');
        img.onload = () => {
          console.log('📸 PhotoUploadChat - Preview generated successfully for:', processedFile.name);
          resolve({ url, error: false });
        };
        img.onerror = () => {
          console.error('📸 PhotoUploadChat - Failed to load preview for:', processedFile.name);
          URL.revokeObjectURL(url);
          resolve({ url: null, error: true });
        };
        img.src = url;
      });
    } catch (error) {
      console.error('📸 PhotoUploadChat - Error creating preview:', error, file?.name);
      return { url: null, error: true };
    }
  }, [isHEIC, convertHEICToJPEG]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    console.log('📸 PhotoUploadChat - Files selected:', {
      count: files.length,
      files: files.map(f => ({ name: f.name, size: f.size, type: f.type })),
      userAgent: navigator.userAgent
    });

    setIsGeneratingPreviews(true);
    
    try {
      // Process files with mobile-specific handling
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const delay = isMobile ? 100 : 0; // Small delay for mobile devices
      
      const processedFiles: File[] = [];
      const previewPromises = files.map(async (file, index) => {
        if (delay && index > 0) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        let processedFile = file;
        // Convert HEIC files before creating preview with graceful fallback
        if (isHEIC(file)) {
          try {
            processedFile = await convertHEICToJPEG(file);
            console.log('📸 PhotoUploadChat - HEIC file converted for upload:', processedFile.name);
          } catch (error) {
            console.error('📸 PhotoUploadChat - HEIC conversion failed, using original:', error);
            // Use original file instead of failing completely
            processedFile = file;
          }
        }
        
        const result = await createPreviewUrl(processedFile);
        return { ...result, processedFile };
      });

      const previewResults = await Promise.all(previewPromises);
      
      // Update state atomically with processed files
      const newUrls = previewResults.map(result => result.url).filter(Boolean) as string[];
      const newErrors = previewResults.map(result => result.error);
      const validFiles = previewResults.map(result => result.processedFile);
      
      setSelectedPhotos(prev => [...prev, ...validFiles]);
      setPreviewUrls(prev => [...prev, ...newUrls]);
      setPreviewErrors(prev => [...prev, ...newErrors]);

      console.log('📸 PhotoUploadChat - Preview generation complete:', {
        successful: newUrls.length,
        errors: newErrors.filter(Boolean).length,
        total: files.length
      });
    } catch (error) {
      console.error('📸 PhotoUploadChat - Error processing files:', error);
    } finally {
      setIsGeneratingPreviews(false);
    }
  };

  const removePhoto = useCallback((index: number) => {
    console.log('📸 PhotoUploadChat - Removing photo at index:', index);
    
    const newPhotos = [...selectedPhotos];
    const newUrls = [...previewUrls];
    const newErrors = [...previewErrors];
    
    // Revoke object URL if it exists
    if (newUrls[index]) {
      URL.revokeObjectURL(newUrls[index]);
    }
    
    newPhotos.splice(index, 1);
    newUrls.splice(index, 1);
    newErrors.splice(index, 1);
    
    setSelectedPhotos(newPhotos);
    setPreviewUrls(newUrls);
    setPreviewErrors(newErrors);

    console.log('📸 PhotoUploadChat - After removal:', {
      remainingPhotos: newPhotos.length,
      photos: newPhotos.map(f => ({ name: f.name, size: f.size }))
    });
  }, [selectedPhotos, previewUrls, previewErrors]);

  const handleContinue = () => {
    console.log('📸 PhotoUploadChat - handleContinue called with photos:', {
      count: selectedPhotos.length,
      photos: selectedPhotos.map(f => ({ name: f.name, size: f.size, type: f.type }))
    });
    
    onPhotosSelected({ photos: selectedPhotos });
  };

  const handleNoPhotos = useCallback(() => {
    console.log('📸 PhotoUploadChat - handleNoPhotos called');
    onPhotosSelected({ photos: [] });
  }, [onPhotosSelected]);

  // Cleanup effect for object URLs
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [previewUrls]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* Photo Grid */}
      {selectedPhotos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {selectedPhotos.map((file, index) => (
            <div key={`${file.name}-${index}`} className="relative">
              {previewUrls[index] && !previewErrors[index] ? (
                <img
                  src={previewUrls[index]}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-20 object-cover rounded-lg border border-white/30"
                  onError={() => {
                    console.error('📸 PhotoUploadChat - Image failed to display:', file.name);
                    const newErrors = [...previewErrors];
                    newErrors[index] = true;
                    setPreviewErrors(newErrors);
                  }}
                />
              ) : (
                <div className="w-full h-20 bg-white/20 rounded-lg border border-white/30 flex items-center justify-center">
                  {isGeneratingPreviews ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full"
                    />
                  ) : previewErrors[index] ? (
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  ) : (
                    <Image className="h-5 w-5 text-white/50" />
                  )}
                </div>
              )}
              <button
                onClick={() => removePhoto(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      <label className="block">
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <div className="w-full p-4 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl hover:bg-white/80 transition-colors cursor-pointer">
          <div className="flex items-center justify-center space-x-2">
            <Upload className="h-5 w-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">
              {selectedPhotos.length > 0 
                ? t('chat.addMorePhotos')
                : t('basicInfo.photosAddLabel')
              }
            </span>
          </div>
        </div>
      </label>

      {/* Action Buttons */}
      <div className="flex gap-2">
        {selectedPhotos.length > 0 ? (
          <button
            onClick={handleContinue}
            className="flex-1 p-3 bg-uplivion-blue text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            {t('buttons.next')} ({selectedPhotos.length} {t('chat.photos')})
          </button>
        ) : (
          <button
            onClick={handleNoPhotos}
            className="flex-1 p-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl text-gray-700 font-medium hover:bg-white/80 transition-colors"
          >
            {t('chat.noPhotos')}
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default PhotoUploadChat;
